import { useEffect, useState } from "react";
import { itemApi } from "../../../api/items";
import { posApi } from "../../../api/pos";
import { unwrapResponse } from "../../../api/client";
import { categoryApi } from "@/api/categories";

interface Product {
  id: string;
  code: string;
  name: string;
  category: string;
  price: number;
  stock: number;
  barcode: string;
  sku: string;
  unit: string;
  image: string;
  itemId?: number;
  itemTypeId?: number;
  uomId?: number;
  warehouseId?: number;
  isSerialized?: boolean;
  taxes?: any[];
  discounts?: any[];
  brandId?: number | null;
  manufacturerId?: number | null;
  categoryIds?: number[];
}

interface ProductSearchModalProps {
  onClose: () => void;
  onAddProduct: (product: Product, quantity: number) => void;
}

const mapItemToProduct = (item: any): Product => ({
  id: String(
    item?.id ??
      item?.itemId ??
      item?.ItemId ??
      item?.code ??
      item?.itemCode ??
      "",
  ),
  code: item?.code ?? item?.itemCode ?? item?.sku ?? item?.barcode ?? "ITEM",
  name: item?.name ?? item?.itemName ?? item?.description ?? "Item",
  category:
    item?.category?.name ??
    item?.categoryName ??
    item?.group?.name ??
    "general",
  price: Number(
    item?.sellingPrice ??
      item?.salesPrice ??
      item?.salePrice ??
      item?.unitPrice ??
      item?.price ??
      0,
  ),
  stock: Number(
    item?.itemStockQuantity ??
      item?.stock ??
      item?.availableQuantity ??
      item?.qty ??
      0,
  ),
  barcode: item?.barcode ?? "",
  sku: item?.sku ?? "",
  unit:
    item?.baseUOM?.name ??
    item?.unit?.name ??
    item?.uomName ??
    item?.uom ??
    "ea",
  image: item?.thumbnail ?? item?.thumbnailImageUrl ?? item?.imageUrl ?? "",
  itemId: Number(item?.id ?? item?.itemId ?? 0) || undefined,
  itemTypeId: Number(item?.itemType?.id ?? item?.itemTypeId ?? 0) || undefined,
  uomId: Number(item?.baseUOM?.id ?? item?.uomId ?? 0) || undefined,
  warehouseId: Number(item?.warehouseId ?? 0) || undefined,
  isSerialized: !!item?.serialized,
  taxes: Array.isArray(item?.salesTaxes) ? item.salesTaxes : [],
  discounts: Array.isArray(item?.salesDiscount) ? item.salesDiscount : [],
  brandId: item?.brand?.id ?? null,
  manufacturerId: item?.manufacturer?.id ?? null,
  categoryIds: Array.isArray(item?.itemCategories)
    ? item.itemCategories.map((c: any) => c?.category?.id).filter(Boolean)
    : [],
});

export default function ProductSearchModal({
  onClose,
  onAddProduct,
}: ProductSearchModalProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [products, setProducts] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState(true);

  const [categories, setCategories] = useState<{ id: string; name: string }[]>([
    { id: "all", name: "All Categories" },
  ]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const data = await categoryApi.getCategories();

        if (data && Array.isArray(data)) {
          const apiCategories = data
            .filter((cat: any) => cat.isActive)
            .map((cat: any) => ({
              id: cat.id,
              name: cat.name,
            }));

          setCategories([
            { id: "all", name: "All Categories" },
            ...apiCategories,
          ]);
        }
      } catch (error) {
        console.error("Failed to fetch categories", error);
      }
    };

    fetchCategories();
  }, []);

  useEffect(() => {
    const resolveWarehouseId = async () => {
      const storedWarehouseId = localStorage.getItem("warehouseId");
      let warehouseId = storedWarehouseId ? Number(storedWarehouseId) : 0;
      if (warehouseId) return warehouseId;

      const outletRaw = localStorage.getItem("outlet");
      if (outletRaw) {
        try {
          const outlet = JSON.parse(outletRaw);
          warehouseId =
            Number(outlet?.warehouse?.id ?? outlet?.warehouseId ?? 0) || 0;
        } catch {
          warehouseId = 0;
        }
      }
      if (warehouseId) {
        localStorage.setItem("warehouseId", String(warehouseId));
        return warehouseId;
      }

      const outletSessionRaw =
        localStorage.getItem("outletSession") ??
        localStorage.getItem("outletSessionInfo");
      if (outletSessionRaw) {
        try {
          const outletSession = JSON.parse(outletSessionRaw);
          warehouseId =
            Number(
              outletSession?.outlet?.warehouse?.id ??
                outletSession?.outlet?.warehouseId ??
                0,
            ) || 0;
        } catch {
          warehouseId = 0;
        }
      }
      if (warehouseId) {
        localStorage.setItem("warehouseId", String(warehouseId));
      }

      if (!warehouseId) {
        let outletId = 0;
        try {
          const outlet = JSON.parse(localStorage.getItem("outlet") || "{}");
          outletId = Number(outlet?.id ?? 0) || 0;
        } catch {
          outletId = 0;
        }
        if (!outletId) {
          try {
            const outletSession = JSON.parse(
              localStorage.getItem("outletSession") || "{}",
            );
            outletId = Number(outletSession?.outlet?.id ?? 0) || 0;
          } catch {
            outletId = 0;
          }
        }
        if (outletId) {
          try {
            const outletDetailsRes = await posApi.getOutletById(outletId);
            const outletDetails = unwrapResponse<any>(outletDetailsRes);
            warehouseId = Number(outletDetails?.warehouse?.id ?? 0) || 0;
            if (warehouseId) {
              localStorage.setItem("warehouseId", String(warehouseId));
              localStorage.setItem("outlet", JSON.stringify(outletDetails));
            }
          } catch {
            warehouseId = 0;
          }
        }
      }
      return warehouseId;
    };

    const query = searchQuery.trim() || "";
    const timer = setTimeout(async () => {
      setIsSearching(true);
      try {
        const warehouseId = await resolveWarehouseId();
        if (!warehouseId) {
          setProducts([]);
          setIsSearching(false);
          return;
        }
        const data = await itemApi.searchItems({
          searchValue: query,
          warehouseId,
          categoryId: null,
        });
        if (Array.isArray(data)) {
          setProducts(data.map(mapItemToProduct));
        } else {
          setProducts([]);
        }
      } catch {
        setProducts([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      searchQuery === "" ||
      product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      product.barcode.includes(searchQuery);

    const matchesCategory =
      selectedCategory === "all" ||
      product.categoryIds?.includes(Number(selectedCategory));

    return matchesSearch && matchesCategory;
  });

  const handleAddToBasket = () => {
    if (selectedProduct) {
      const qty = parseFloat(quantities[selectedProduct.id] || "1");
      if (qty > 0) {
        onAddProduct(selectedProduct, qty);
        onClose();
      }
    }
  };

  const updateQuantity = (productId: string, value: string) => {
    setQuantities((prev) => ({ ...prev, [productId]: value }));
  };

  const handleSelectProduct = (product: Product) => {
    setSelectedProduct(product);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] min-h-[300px] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
              <i className="ri-search-line text-teal-600 text-xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 whitespace-nowrap">
                Product Search
              </h2>
              <p className="text-sm text-gray-600 whitespace-nowrap">
                Browse and add products to basket
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-lg transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl text-gray-600"></i>
          </button>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-gray-200 space-y-4">
          <div className="relative">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by name, code, or barcode..."
              className="w-full pl-12 pr-4 py-3 border-2 border-gray-300 rounded-lg text-sm focus:outline-none focus:border-teal-500"
              autoFocus
            />
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">
                Category:
              </span>
              <div className="flex gap-2 flex-wrap">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      selectedCategory === cat.id
                        ? "bg-teal-600 text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-4 gap-4">
            {filteredProducts.map((product) => {
              const isSelected = selectedProduct?.id === product.id;
              const qty = quantities[product.id] || "1";

              return (
                <div
                  key={product.id}
                  onClick={() =>
                    product.stock > 0 && handleSelectProduct(product)
                  }
                  className={`relative bg-white border-2 rounded-xl overflow-hidden transition-all ${
                    product.stock === 0
                      ? "border-gray-200 opacity-60 cursor-not-allowed"
                      : isSelected
                        ? "border-teal-500 shadow-lg cursor-pointer"
                        : "border-gray-200 hover:border-gray-300 hover:shadow-md cursor-pointer"
                  }`}
                >
                  {/* Product Image */}
                  <div className="w-full h-48 bg-gray-100 overflow-hidden relative">
                    {product.image ? (
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover object-top"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <i className="ri-image-line text-3xl"></i>
                      </div>
                    )}

                    {/* Overlay with Quantity and Add Button - Only show when selected */}
                    {isSelected && (
                      <div
                        className="absolute inset-0 bg-white/95 flex flex-col items-center justify-center p-4"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="w-full space-y-3">
                          <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-2 text-center whitespace-nowrap">
                              Quantity
                            </label>
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() =>
                                  updateQuantity(
                                    product.id,
                                    Math.max(
                                      0.1,
                                      parseFloat(qty) -
                                        (product.unit === "kg" ? 0.1 : 1),
                                    ).toFixed(1),
                                  )
                                }
                                className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-bold transition-colors cursor-pointer flex-shrink-0"
                              >
                                <i className="ri-subtract-line text-lg"></i>
                              </button>
                              <input
                                type="number"
                                onWheel={(e) => e.currentTarget.blur()}
                                value={qty}
                                onChange={(e) =>
                                  updateQuantity(product.id, e.target.value)
                                }
                                className="w-24 px-3 py-2.5 text-center border-2 border-gray-300 rounded-lg focus:outline-none focus:border-teal-500 text-lg font-bold"
                                step={product.unit === "kg" ? "0.1" : "1"}
                                min="0.1"
                              />
                              <button
                                onClick={() =>
                                  updateQuantity(
                                    product.id,
                                    (
                                      parseFloat(qty) +
                                      (product.unit === "kg" ? 0.1 : 1)
                                    ).toFixed(1),
                                  )
                                }
                                className="w-10 h-10 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-bold transition-colors cursor-pointer flex-shrink-0"
                              >
                                <i className="ri-add-line text-lg"></i>
                              </button>
                            </div>
                          </div>

                          <button
                            onClick={() => handleAddToBasket()}
                            className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-bold transition-colors cursor-pointer whitespace-nowrap text-sm"
                          >
                            <i className="ri-shopping-cart-line mr-2"></i>
                            Add to Basket
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="p-4 space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono whitespace-nowrap">
                        {product.code}
                      </span>
                      {product.sku && (
                        <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded text-xs font-mono whitespace-nowrap">
                          {product.sku}
                        </span>
                      )}
                    </div>

                    <h3 className="text-sm font-bold text-gray-900 line-clamp-2 min-h-[2.5rem]">
                      {product.name}
                    </h3>

                    <div className="flex items-center justify-between">
                      <div
                        className={`flex items-center gap-1 text-xs font-medium ${product.stock === 0 ? "text-red-600" : "text-gray-600"}`}
                      >
                        <i className="ri-stack-line"></i>
                        <span className="whitespace-nowrap">
                          {product.stock === 0
                            ? "Out of Stock"
                            : `Stock: ${product.stock}`}
                        </span>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-gray-900 whitespace-nowrap">
                          {product.price.toFixed(2)}
                        </p>
                        <p className="text-xs text-gray-500 whitespace-nowrap">
                          per {product.unit}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Loading */}
          {isSearching && (
            <div className="flex items-center justify-center py-6 text-gray-500 text-sm">
              <i className="ri-loader-4-line mr-2 animate-spin text-lg"></i>
              Loading products...
            </div>
          )}

          {/* Empty State (only when NOT loading) */}
          {!isSearching && filteredProducts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16">
              <i className="ri-inbox-line text-6xl text-gray-300 mb-4"></i>

              <p className="text-lg font-semibold text-gray-600 mb-1">
                {searchQuery ? "No products found" : "No products available"}
              </p>

              <p className="text-sm text-gray-500">
                {searchQuery
                  ? "Try adjusting your search or filters"
                  : "Products will appear here once loaded"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
